/**
 * Snowflake / warehouse statement result shape for feature metadata (JSON_ARRAY).
 * Optional parser for warehouse exports — runtime UI uses GET `/api/features` via `parseFeaturesApiResponse`.
 */
export interface FeatureStatementColumnSchema {
  name: string;
  type_text: string;
  type_name: string;
  position: number;
}

export interface FeatureMetadataStatementJson {
  statement_id: string;
  status: { state: string };
  manifest: {
    format: string;
    schema: {
      column_count: number;
      columns: FeatureStatementColumnSchema[];
    };
    total_chunk_count: number;
    chunks: Array<{ chunk_index: number; row_offset: number; row_count: number }>;
    total_row_count: number;
    truncated: boolean;
  };
  result: {
    chunk_index: number;
    row_offset: number;
    row_count: number;
    data_array: string[][];
  };
}

/** Warehouse column typing + ordinal position (from `manifest.schema.columns`). */
export type FeatureMetadataColumnMeta = Pick<
  FeatureStatementColumnSchema,
  'position' | 'type_text' | 'type_name'
>;

/** One logical feature row (typed fields from `data_array`). */
export interface FeatureMetadataRow {
  featureId: number;
  featureOrder: number;
  description: string;
  category: string;
  uiName: string;
  apiName: string;
  dbName: string;
  /** UI control hint from warehouse (`range`, `dropdown`, `time`, `text`, …). */
  uiDataType: string;
  dbDataType: string;
  isEditable: boolean;
  isActive: boolean;
  /** Slider lower bound from catalogue (`DTB_FEATURES_MASTER`); UI default when absent. */
  rangeMin: number | null;
  /** Slider upper bound from catalogue; UI default when absent. */
  rangeMax: number | null;
  /**
   * Child feature IDs whose values are driven or re-derived when this feature changes
   * (Mongo `dependentsFeatureIds` / warehouse catalogue). UI lists dependents sorted by
   * category (Captain → Crew → Ship → Other), then each child's `featureOrder`.
   */
  dependentsFeatureIds: readonly number[];
  /**
   * For each warehouse column name, its `position`, `type_text`, and `type_name`
   * (same map on every row — shared reference from parse).
   */
  columnMetaByName: Readonly<Record<string, FeatureMetadataColumnMeta>>;
}

/** Result of parsing a full statement JSON (rows + manifest schema columns). */
export interface FeatureMetadataParseResult {
  rows: FeatureMetadataRow[];
  /** Sorted by `position`; same definitions used for `columnMetaByName` on each row. */
  schemaColumns: FeatureStatementColumnSchema[];
}

/** Single feature document from GET `/api/features` (Mongo + catalogue fields). */
export interface FeatureMetadataApiDto {
  _id?: string;
  featureId: number;
  featureOrder?: number;
  category: string;
  description?: string;
  uiName?: string;
  apiName: string;
  dbName: string;
  uiDataType?: string;
  dbDataType?: string;
  isEditable?: boolean;
  isActive?: boolean;
  rangeMin?: number | null;
  rangeMax?: number | null;
  /** Feature IDs that depend on this feature (catalogue / Mongo). */
  dependentsFeatureIds?: number[] | null;
}

/** `data` payload from GET `/api/features` — buckets match app-service `FEATURE_CATEGORY_KEY`. */
export interface FeaturesGroupedApiData {
  captain: FeatureMetadataApiDto[];
  crew: FeatureMetadataApiDto[];
  ship: FeatureMetadataApiDto[];
  otherContributingFactors: FeatureMetadataApiDto[];
}

/** Response envelope from GET `/api/features`. */
export interface FeaturesApiResponse {
  success: boolean;
  message: string;
  data: FeaturesGroupedApiData;
}

const EMPTY_COLUMN_META: Readonly<Record<string, FeatureMetadataColumnMeta>> = Object.freeze({});

/** One bucket after grouping by `category` (e.g. all rows where category === `"Factors - Ship"`). */
export interface FeatureMetadataCategoryGroup {
  category: string;
  items: FeatureMetadataRow[];
}

const FACTORS_CATEGORY_PREFIX = 'Factors - ';

/** Parsed label for UI / filtering (e.g. `"Factors - Ship"` → facet `"Ship"`). */
export interface FeatureCategoryParts {
  /** Original `category` string from the row. */
  raw: string;
  /** `"factors"` for `Factors - *`, otherwise `"other"`. */
  kind: 'factors' | 'other';
  /**
   * Sub-label after `"Factors - "` when `kind === 'factors'`
   * (e.g. `"Ship"`, `"Captain"`, `"Crew"`).
   */
  factorsFacet?: string;
}

/**
 * Interprets warehouse `category` strings:
 * - `"Factors - Ship"` → `{ kind: 'factors', factorsFacet: 'Ship' }`
 * - `"Other contributing factors"` → `{ kind: 'other' }`
 */
export function parseFeatureCategoryLabel(category: string): FeatureCategoryParts {
  const raw = category.trim();
  if (raw.startsWith(FACTORS_CATEGORY_PREFIX)) {
    return {
      raw,
      kind: 'factors',
      factorsFacet: raw.slice(FACTORS_CATEGORY_PREFIX.length).trim() || undefined,
    };
  }
  return { raw, kind: 'other' };
}

/**
 * Groups rows by exact `category` (e.g. `"Factors - Ship"`).
 * Items inside each group are sorted by `featureOrder`, then `featureId`.
 */
export function groupFeatureMetadataByCategory(
  rows: readonly FeatureMetadataRow[],
): FeatureMetadataCategoryGroup[] {
  const map = new Map<string, FeatureMetadataRow[]>();
  for (const row of rows) {
    const key = row.category.trim() || '(Uncategorised)';
    let bucket = map.get(key);
    if (!bucket) {
      bucket = [];
      map.set(key, bucket);
    }
    bucket.push(row);
  }
  const groups: FeatureMetadataCategoryGroup[] = [...map.entries()].map(([category, items]) => ({
    category,
    items: items.slice().sort((a, b) => a.featureOrder - b.featureOrder || a.featureId - b.featureId),
  }));
  groups.sort((a, b) => a.category.localeCompare(b.category, undefined, { sensitivity: 'base' }));
  return groups;
}

function parseBool(s: string): boolean {
  const t = s.trim().toLowerCase();
  return t === 'true' || t === '1';
}

function cellAt(row: string[], columns: FeatureStatementColumnSchema[], name: string): string {
  const col = columns.find((c) => c.name === name);
  if (!col) {
    return '';
  }
  return row[col.position] ?? '';
}

function optionalNumberCell(
  row: string[],
  columns: FeatureStatementColumnSchema[],
  name: string,
): number | null {
  const v = cellAt(row, columns, name).trim();
  if (v === '') {
    return null;
  }
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function parseDependentsFeatureIdsFromRow(
  row: string[],
  columns: FeatureStatementColumnSchema[],
): number[] {
  const col = columns.find((c) => c.name === 'dependentsFeatureIds');
  if (!col) {
    return [];
  }
  const raw = (row[col.position] ?? '').trim();
  if (!raw) {
    return [];
  }
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (Array.isArray(parsed)) {
      return parsed.map((x) => Number(x)).filter((n) => Number.isFinite(n));
    }
  } catch {
    /* ignore malformed JSON */
  }
  return [];
}

function normaliseDependentsFeatureIds(value: unknown): number[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.map((x) => Number(x)).filter((n) => Number.isFinite(n));
}

function buildColumnMetaByName(
  columns: FeatureStatementColumnSchema[],
): Readonly<Record<string, FeatureMetadataColumnMeta>> {
  const out: Record<string, FeatureMetadataColumnMeta> = {};
  for (const c of columns) {
    out[c.name] = {
      position: c.position,
      type_text: c.type_text,
      type_name: c.type_name,
    };
  }
  return out;
}

/**
 * Maps `result.data_array` rows to typed objects using `manifest.schema.columns` positions,
 * and attaches each column’s `position` / `type_text` / `type_name` via `columnMetaByName`.
 */
export function parseFeatureMetadataStatement(json: FeatureMetadataStatementJson): FeatureMetadataParseResult {
  const columns = [...json.manifest.schema.columns].sort((a, b) => a.position - b.position);
  const columnMetaByName = buildColumnMetaByName(columns);
  const rows: FeatureMetadataRow[] = json.result.data_array.map((row) => ({
    featureId: Number(cellAt(row, columns, 'featureId')),
    featureOrder: Number(cellAt(row, columns, 'featureOrder')),
    description: cellAt(row, columns, 'description'),
    category: cellAt(row, columns, 'category'),
    uiName: cellAt(row, columns, 'uiName'),
    apiName: cellAt(row, columns, 'apiName'),
    dbName: cellAt(row, columns, 'dbName'),
    uiDataType: cellAt(row, columns, 'uiDataType'),
    dbDataType: cellAt(row, columns, 'dbDataType'),
    isEditable: parseBool(cellAt(row, columns, 'isEditable')),
    isActive: parseBool(cellAt(row, columns, 'isActive')),
    rangeMin: optionalNumberCell(row, columns, 'rangeMin'),
    rangeMax: optionalNumberCell(row, columns, 'rangeMax'),
    dependentsFeatureIds: parseDependentsFeatureIdsFromRow(row, columns),
    columnMetaByName,
  }));
  return { rows, schemaColumns: columns };
}

function mapApiDtoToFeatureMetadataRow(dto: FeatureMetadataApiDto): FeatureMetadataRow {
  const rMin = dto.rangeMin;
  const rMax = dto.rangeMax;
  return {
    featureId: Number(dto.featureId),
    featureOrder: Number(dto.featureOrder ?? 0),
    description: String(dto.description ?? ''),
    category: String(dto.category ?? ''),
    uiName: String(dto.uiName ?? ''),
    apiName: String(dto.apiName ?? '').trim() || `feature_${dto.featureId}`,
    dbName: String(dto.dbName ?? ''),
    uiDataType: String(dto.uiDataType ?? ''),
    dbDataType: String(dto.dbDataType ?? ''),
    isEditable: Boolean(dto.isEditable),
    isActive: Boolean(dto.isActive),
    rangeMin: rMin != null && Number.isFinite(Number(rMin)) ? Number(rMin) : null,
    rangeMax: rMax != null && Number.isFinite(Number(rMax)) ? Number(rMax) : null,
    dependentsFeatureIds: normaliseDependentsFeatureIds(dto.dependentsFeatureIds),
    columnMetaByName: EMPTY_COLUMN_META,
  };
}

/**
 * Flattens grouped GET `/api/features` payload into the same `FeatureMetadataRow[]` shape used by the overview form.
 * Order: captain → crew → ship → other contributing factors (each bucket already sorted by `featureOrder` on the server).
 */
export function parseFeaturesApiResponse(data: FeaturesGroupedApiData): FeatureMetadataParseResult {
  const buckets: FeatureMetadataApiDto[] = [
    ...(data.captain ?? []),
    ...(data.crew ?? []),
    ...(data.ship ?? []),
    ...(data.otherContributingFactors ?? []),
  ];
  const rows = buckets.map(mapApiDtoToFeatureMetadataRow);
  return { rows, schemaColumns: [] };
}
